import { z } from 'zod';
import { BaseResponseSchema, CreateWaferRequestSchema, CreateWaferResponseSchema, DateWindowSchema, DeleteWaferFilesResponseSchema, FileDeleteRequestSchema, GetWaferByIdResponseSchema, GetWaferSummariesRequestSchema, GetWaferSummariesResponseSchema, GetWafersRequestSchema, GetWafersResponseSchema, UpdateWaferRequestSchema, UpdateWaferResponseSchema, ValidateFingerprintsRequestSchema, ValidateFingerprintsResponseSchema, WaferHistoryRequestSchema, WaferHistoryResponseSchema, WaferLocationHistoryResponseSchema, WaferSearchRequestSchema, WaferSearchResponseSchema, WaferSummaryStatsSchema, WaferTransferRequestSchema, WaferTransferResponseSchema, } from '../../schemas.js';
import { VerifyFingerprintImageRequestSchema, MarkFingerprintImageBadRequestSchema, ResolveOcrConflictsRequestSchema, FingerprintImageActionResponseSchema, ResolveOcrConflictsFlatResponseSchema, } from '../../schemas/wafer-fingerprint.schemas.js';
import { WaferFingerprintWaferPathParamSchema, WaferFingerprintImagePathParamSchema, WaferIdParamSchema, } from '../../schemas/params.js';
import { ClearLearnedLightingResponseSchema } from '../../efem-wafer-imaging.schemas.js';
/** REST contract for `insight-api/src/routes/tenant/wafers.routes.ts` and fingerprint image routes. */
export const wafersContract = {
    list: {
        method: 'GET',
        path: '/tenant/wafers',
        query: GetWafersRequestSchema,
        response: GetWafersResponseSchema,
        responseStyle: 'unwrap-data',
        queryLimitMax: 100,
        errorMessage: 'Failed to fetch wafers',
    },
    summaries: {
        method: 'GET',
        path: '/tenant/wafers/summaries',
        query: GetWaferSummariesRequestSchema,
        response: GetWaferSummariesResponseSchema,
        responseStyle: 'unwrap-data',
        queryLimitMax: 100,
        dedupe: true,
        errorMessage: 'Failed to fetch wafer summaries',
    },
    search: {
        method: 'POST',
        path: '/tenant/wafers/search',
        body: WaferSearchRequestSchema,
        response: WaferSearchResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to search wafers',
    },
    create: {
        method: 'POST',
        path: '/tenant/wafers',
        body: CreateWaferRequestSchema,
        response: CreateWaferResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to create wafer',
    },
    getById: {
        method: 'GET',
        path: '/tenant/wafers/:id',
        pathParams: WaferIdParamSchema,
        response: GetWaferByIdResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to fetch wafer',
    },
    update: {
        method: 'PUT',
        path: '/tenant/wafers/:id',
        pathParams: WaferIdParamSchema,
        body: UpdateWaferRequestSchema,
        response: UpdateWaferResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to update wafer',
    },
    delete: {
        method: 'DELETE',
        path: '/tenant/wafers/:id',
        pathParams: WaferIdParamSchema,
        response: BaseResponseSchema,
        responseStyle: 'void',
        errorMessage: 'Failed to delete wafer',
    },
    transfer: {
        method: 'POST',
        path: '/tenant/wafers/transfer',
        body: WaferTransferRequestSchema,
        response: WaferTransferResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to transfer wafer',
    },
    validateFingerprints: {
        method: 'POST',
        path: '/tenant/wafers/validate-fingerprints',
        body: ValidateFingerprintsRequestSchema,
        response: ValidateFingerprintsResponseSchema,
        responseStyle: 'parse-response',
    },
    deleteFiles: {
        method: 'DELETE',
        path: '/tenant/wafers/:id/files',
        pathParams: WaferIdParamSchema,
        body: FileDeleteRequestSchema,
        response: DeleteWaferFilesResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to delete wafer files',
    },
    getHistory: {
        method: 'GET',
        path: '/tenant/wafers/:id/history',
        pathParams: WaferIdParamSchema,
        query: WaferHistoryRequestSchema,
        response: WaferHistoryResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to fetch wafer history',
    },
    getHistoryStats: {
        method: 'GET',
        path: '/tenant/wafers/:id/history/stats',
        pathParams: WaferIdParamSchema,
        response: WaferSummaryStatsSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to fetch wafer history stats',
    },
    getLocationHistory: {
        method: 'GET',
        path: '/tenant/wafers/:id/location-history',
        pathParams: WaferIdParamSchema,
        query: DateWindowSchema,
        response: WaferLocationHistoryResponseSchema,
        responseStyle: 'unwrap-data',
        errorMessage: 'Failed to fetch wafer location history',
    },
    exportHistory: {
        method: 'GET',
        path: '/tenant/wafers/:id/export/history',
        pathParams: WaferIdParamSchema,
        query: DateWindowSchema,
        response: z.unknown(),
        responseStyle: 'blob',
        timeoutMs: 60000,
        errorMessage: 'Failed to export wafer history',
    },
    clearLearnedLighting: {
        method: 'DELETE',
        path: '/tenant/wafers/:id/learned-lighting',
        pathParams: WaferIdParamSchema,
        response: ClearLearnedLightingResponseSchema,
        responseStyle: 'void',
    },
    verifyFingerprintImage: {
        method: 'PATCH',
        path: '/tenant/wafers/:waferId/fingerprint-images/:imageId/verify',
        pathParams: WaferFingerprintImagePathParamSchema,
        body: VerifyFingerprintImageRequestSchema,
        response: FingerprintImageActionResponseSchema,
        responseStyle: 'parse-response',
        errorMessage: 'Failed to verify fingerprint image',
    },
    markFingerprintImageBad: {
        method: 'PATCH',
        path: '/tenant/wafers/:waferId/fingerprint-images/:imageId/mark-bad',
        pathParams: WaferFingerprintImagePathParamSchema,
        body: MarkFingerprintImageBadRequestSchema,
        response: FingerprintImageActionResponseSchema,
        responseStyle: 'parse-response',
        errorMessage: 'Failed to mark fingerprint image as bad',
    },
    resolveOcrConflicts: {
        method: 'POST',
        path: '/tenant/wafers/:waferId/fingerprint-images/resolve-conflicts',
        pathParams: WaferFingerprintWaferPathParamSchema,
        body: ResolveOcrConflictsRequestSchema,
        response: ResolveOcrConflictsFlatResponseSchema,
        responseStyle: 'parse-response',
        errorMessage: 'Failed to resolve OCR conflicts',
    },
};
//# sourceMappingURL=wafers.contract.js.map